/**
 * Arena **fire-mage skill / modifier / passive-stat / XP tuning tables** and
 * their pure math (Arena minigame, PRD #334; fire-mage rework, ADR-0179 -
 * superseding ADR-0175's three-weapon arsenal).
 *
 * The build system is Path-of-Exile-shaped: the runner owns exactly **one base
 * skill** - Magic Arrow, a nearest-enemy auto-fire projectile - and the
 * level-up draft offers five **leveled modifiers** that reshape it
 * ({@link ARENA_MODIFIER_IDS}): more projectiles, the fireball transformation,
 * an on-hit explosion, chaining, and a flame-strike proc on secondary hits.
 * Modifiers multiply *together* (five projectiles that each chain and explode),
 * so every damage lever here is a POE-style named constant - "less" multipliers
 * on breadth, per-hop decay on chains, fractions on splash - and the six
 * draftable passive stats scale the whole kit.
 *
 * Everything here is a pure function of its arguments - no world, no rng, no
 * clock - so it unit-tests trivially and the firing/draft machinery in
 * {@link ./arena} stays the only stateful consumer. Every number is a **named
 * tuning surface** (the `ARENA_STAGES` discipline). The balance floor
 * (asserted by the balance-guard test, retuned in ADR-0184's bigger-waves
 * pass): the bare skill **one-shots a tier-1 monster** and its volley rate
 * clears **at least half** the opening spawn rate - the horde outpaces a
 * never-drafting runner from the opening wave by design (kiting + drafting
 * are mandatory; the packed field is what feeds the multi-projectile/chain
 * builds). Growth compounds *from the base* (modifier and stat multipliers
 * apply to the table numbers, never to a running value), so re-deriving any
 * effective figure is idempotent.
 */

// ---------------------------------------------------------------------------
// The base skill. One skill, fixed - the modifiers are the build.
// ---------------------------------------------------------------------------

/** The fire mage's one base skill (also the baked effect-art id of its projectile). */
export const ARENA_SKILL_ID = 'magic-arrow';

/** Display name for the run HUD / draft overlay while un-transformed. */
export const ARENA_SKILL_NAME = 'Magic Arrow';

/** Display name once Greater Projectile has transformed the skill. */
export const ARENA_FIREBALL_NAME = 'Fireball';

/** Display name once the Comet keystone has transcended the fireball (PRD #419
 * slice E - the `ARENA_FIRE_SERPENT_NAME` precedent, whose keystone this replaces). */
export const ARENA_COMET_NAME = 'Comet';

/**
 * Base damage per primary projectile. One-shots a stage-0 rat (20 HP) - the
 * balance-guard invariant the old starting crossbow established.
 */
export const ARENA_SKILL_DAMAGE = 20;

/**
 * Base ticks between volleys (25 = 1.25s at 20Hz) - one kill per cooldown
 * exactly matches the stage-0 spawn rate (4 per 5s wave).
 */
export const ARENA_SKILL_COOLDOWN_TICKS = 25;

/** Base targeting range in tiles (Chebyshev, the combat range metric). */
export const ARENA_SKILL_RANGE = 10;

/** Hard floor on the effective volley cooldown, whatever the stat stack. */
export const ARENA_MIN_SKILL_COOLDOWN_TICKS = 5;

// ---------------------------------------------------------------------------
// The five modifiers (the draft pool's build-shaping half).
// ---------------------------------------------------------------------------

/**
 * The draftable Arena modifiers across **every** class (PRD #446): this is the
 * cross-class "any modifier id" type, keyed by `ArenaBuild.modifiers` and read
 * generically by the shared draft pool / passive machinery. Each class's def
 * declares which subset its own pool draws from (`ArenaClassDef.modifierIds`),
 * so a fire run never offers an archer line and vice-versa.
 *
 * The fire mage's seven (`more-projectiles`…`scorched-earth`; `nova`/`scorched-earth`
 * joined in PRD #378's pool expansion) sit first; the **archer's seven** (PRD #446
 * Slice 1, ADR-0237) follow. A class's math module reads only its own ids off
 * the shared `mods` record - the fire cascade never looks up an archer id.
 */
export type ArenaModifierId =
  | 'more-projectiles'
  | 'greater-projectile'
  | 'explosion'
  | 'chain'
  | 'flame-strike'
  | 'nova'
  | 'scorched-earth'
  // Archer (PRD #446 Slice 1, ADR-0237) - see {@link ./arenaClasses/archer}.
  | 'double-shot'
  | 'heavy-draw'
  | 'ricochet'
  | 'barbed-arrows'
  | 'poison-arrows'
  | 'eagle-eye'
  | 'caltrops';

/** Every modifier id, in the fixed order the draft pool is built in. */
export const ARENA_MODIFIER_IDS: readonly ArenaModifierId[] = [
  'more-projectiles',
  'greater-projectile',
  'explosion',
  'chain',
  'flame-strike',
  'nova',
  'scorched-earth',
];

/** Highest level any modifier can be drafted to. */
export const ARENA_MODIFIER_MAX_LEVEL = 5;

/** The build's modifier levels, keyed by id (absent = level 0 = not drafted). */
export type ArenaModifierLevels = Partial<Record<ArenaModifierId, number>>;

/** The owned keystone transcendences, keyed by the modifier they transform
 * (PRD #378: one binary keystone per maxed modifier, at most). */
export type ArenaKeystones = ReadonlySet<ArenaModifierId>;

/** What the skill's projectile looks like on the wire/screen: an arrow until
 * Greater Projectile transforms it into a fireball. `fire-snake` is the
 * piercing serpent art the Serpent Spiral keystone's nova rays still fly as
 * (PRD #378) - the base skill itself stopped wearing it when Comet replaced
 * the Fire Serpent keystone (PRD #419 slice E).
 *
 * The archer (PRD #446 Slice 1, ADR-0237) adds two **physical item-art** looks
 * distinct from the mage's magic `arrow` (which renders the glowing `magic-arrow`
 * effect): `bow-arrow` is a feathered arrow (item art 0x0F42), `bolt` a crossbow
 * bolt (0x1BFE) - both rotated client-side along the flight vector. Named to
 * avoid colliding with the mage's `arrow` look. */
export type ArenaProjectileKind = 'arrow' | 'fireball' | 'fire-snake' | 'bow-arrow' | 'bolt';

/** +projectiles per more-projectiles level (level 5 = 6-projectile volleys). */
export const ARENA_PROJECTILES_PER_LEVEL = 1;

/**
 * POE-style "less" multiplier on EVERY projectile while more-projectiles is
 * owned: 6 projectiles deal 6 x 0.8 = 4.8x, not 6x - breadth costs a little
 * depth, so the modifier is a clear win without trivializing single targets.
 */
export const ARENA_MULTI_PROJECTILE_LESS_FACTOR = 0.8;

/**
 * Greater Projectile: +damage fraction per level, applied to the base (level 1
 * transforms the arrow into a fireball at 30 damage; level 5 lands 70).
 */
export const ARENA_GREATER_DAMAGE_PER_LEVEL = 0.5;

/** Explosion radius in tiles around the struck enemy at level 1. */
export const ARENA_EXPLOSION_RADIUS_BASE = 1;

/** Extra explosion radius per level above 1 (level 5 = 3 tiles). */
export const ARENA_EXPLOSION_RADIUS_PER_LEVEL = 0.5;

/** Fraction of the triggering hit the splash deals at level 1. */
export const ARENA_EXPLOSION_FRACTION_BASE = 0.4;

/** Extra splash fraction per level above 1. */
export const ARENA_EXPLOSION_FRACTION_PER_LEVEL = 0.1;

/** Cap on the splash fraction - splash never rivals the direct hit. */
export const ARENA_EXPLOSION_FRACTION_CAP = 0.8;

/** Chain hops per primary hit per chain level (level 3 = 3 hops). */
export const ARENA_CHAIN_HOPS_PER_LEVEL = 1;

/**
 * Per-hop damage decay: hop `h` deals `primary x FACTOR^h`, derived from the
 * primary hit each time (never a running value - the derive-from-base rule).
 */
export const ARENA_CHAIN_DAMAGE_FACTOR = 0.6;

/** Max tiles a chain hop can jump from the enemy it leaves (Chebyshev). */
export const ARENA_CHAIN_RANGE = 5;

/**
 * Flame-strike proc chance per level on each SECONDARY hit (a chained hit or a
 * non-first projectile of a volley - the "extra projectile" rule). Level 5 = 1.0
 * = guaranteed.
 */
export const ARENA_FLAME_STRIKE_CHANCE_PER_LEVEL = 0.2;

/** Fraction of the triggering hit the flame-strike column deals to everything in it. */
export const ARENA_FLAME_STRIKE_DAMAGE_FRACTION = 0.6;

/** Column radius in tiles around the struck tile (the struck enemy IS included - bonus damage). */
export const ARENA_FLAME_STRIKE_RADIUS = 1;

// --- Nova (PRD #378): every Nth volley rings projectiles out from the runner.
// Only good inside the pack - the risk/reward proximity axis the kit lacked;
// with stop-to-cast (ADR-0191) a nova build is "wade in, plant your feet,
// survive the cast".

/** Volleys per nova ring at level 1 (every 6th volley). */
export const ARENA_NOVA_INTERVAL_BASE = 6;

/** Volleys shaved off the interval per level above 1 (level 5 = every 2nd volley). */
export const ARENA_NOVA_INTERVAL_STEP = 1;

/** The ring's reach in tiles at base area: broad enough to clear the pack
 * around a planted caster while still rewarding deliberate positioning. */
export const ARENA_NOVA_RANGE = 8;

/** Each ray's damage as a fraction of the primary projectile's. */
export const ARENA_NOVA_DAMAGE_FACTOR = 0.8;

/** Volleys between nova rings for the drafted level (0 while unowned = never). */
export function novaInterval(mods: ArenaModifierLevels): number {
  const level = mods.nova ?? 0;
  if (level <= 0) return 0;
  return Math.max(1, ARENA_NOVA_INTERVAL_BASE - ARENA_NOVA_INTERVAL_STEP * (level - 1));
}

/** The ring's reach in tiles (0 while unowned), area-stat scaled like every reach. */
export function novaRange(mods: ArenaModifierLevels, areaStatLevel: number): number {
  if ((mods.nova ?? 0) <= 0) return 0;
  return ARENA_NOVA_RANGE * (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel);
}

/** One nova ray's damage, derived from the volley's projectile damage. */
export function novaRayDamage(projectileDamage: number): number {
  return Math.round(projectileDamage * ARENA_NOVA_DAMAGE_FACTOR);
}

// --- Scorched Earth (PRD #378): the runner's walked tiles burn behind them. -

/**
 * Ticks between burn applications of every ground fire zone (Scorched Earth
 * trail now; Cataclysm ground later): standing in fire hurts twice a second.
 */
export const ARENA_ZONE_BURN_INTERVAL_TICKS = 10;

/** Ticks a Scorched Earth trail tile burns at level 1. */
export const ARENA_SCORCHED_DURATION_BASE_TICKS = 40;

/** Extra burn ticks per Scorched Earth level above 1 (level 5 = 6 seconds). */
export const ARENA_SCORCHED_DURATION_PER_LEVEL_TICKS = 0;

/** Perpendicular trail width by level: early picks stay narrow; late picks cover the pack. */
export function scorchedTrailWidth(mods: ArenaModifierLevels): number {
  const level = mods['scorched-earth'] ?? 0;
  if (level <= 0) return 0;
  return level >= 5 ? 5 : level >= 3 ? 3 : 1;
}

/**
 * Fraction of the build's live projectile damage ({@link skillProjectileDamage})
 * one burn deals at level 1 - applied per **entry** (a tile change onto a
 * burning tile) and per **interval tick** (dwelling). Derived, never absolute
 * (PRD #419): the old flat constant (3 + 2/level) was the kit's only
 * arithmetic-dead damage source and decayed to a rounding error against the
 * geometric tier HP curve. Tuned against the crossing-tax guard test (one
 * crossing = 4%..35% of the tier's per-enemy HP under the guard's build model).
 */
export const ARENA_SCORCHED_FRACTION_BASE = 0.2;

/** Extra fraction per Scorched Earth level above 1 (level 5 = 0.4). */
export const ARENA_SCORCHED_FRACTION_PER_LEVEL = 0.05;

/** Ticks a Scorched Earth trail tile burns for (0 while the modifier is unowned). */
export function scorchedTrailDurationTicks(mods: ArenaModifierLevels): number {
  const level = mods['scorched-earth'] ?? 0;
  if (level <= 0) return 0;
  return (
    ARENA_SCORCHED_DURATION_BASE_TICKS + ARENA_SCORCHED_DURATION_PER_LEVEL_TICKS * (level - 1)
  );
}

/** The trail's burn fraction for the drafted level (0 while unowned). */
export function scorchedTrailFraction(mods: ArenaModifierLevels): number {
  const level = mods['scorched-earth'] ?? 0;
  if (level <= 0) return 0;
  return ARENA_SCORCHED_FRACTION_BASE + ARENA_SCORCHED_FRACTION_PER_LEVEL * (level - 1);
}

/**
 * Damage one burn (entry or interval) of a trail tile deals: the leveled
 * fraction of the build's **live** projectile damage at drop time - the whole
 * damage stack (Greater Projectile, the damage stat, ascensions) scales the
 * trail for free, and re-deriving is idempotent (derive-from-base as ever).
 */
export function scorchedTrailDamage(mods: ArenaModifierLevels, projectileDamage: number): number {
  const fraction = scorchedTrailFraction(mods);
  if (fraction <= 0) return 0;
  return Math.round(projectileDamage * fraction);
}

// ---------------------------------------------------------------------------
// Keystone transcendences (PRD #378): one binary draft per maxed modifier that
// qualitatively transforms it. Owned keystones live as a Set on ArenaBuild;
// all the math stays pure here, every lever a named constant.
// ---------------------------------------------------------------------------

/** The keystone ids, for display - each is 1:1 with the modifier it transforms.
 * `comet` replaced `fire-serpent` on the Greater Projectile line (PRD #419
 * slice E); builds are run-scoped state, so the retirement needed no migration. */
export type ArenaKeystoneId =
  | 'comet'
  | 'fork'
  | 'cataclysm'
  | 'meteor'
  | 'serpent-spiral'
  | 'combustion'
  | 'splinter'
  // Archer keystones (PRD #446 Slice 1, ADR-0237). Declared for the type; they
  // TRAIL Slice 1 (absent from the archer def's `keystones` map, so unofferable
  // until their own follow-up PR lands - the #378 incremental rule).
  | 'arrow-storm'
  | 'endless-ricochet'
  | 'headshot'
  | 'hemorrhage';

/**
 * Which modifier unlocks which keystone at max level. Every modifier line ends
 * in one since Splinter filled the last slot (PRD #419 slice D); the map stays
 * Partial so a future line can ship keystone-less again (the PRD's
 * ship-incrementally rule) - the draft pool skips an absent entry.
 */
export const ARENA_KEYSTONES: Partial<Record<ArenaModifierId, ArenaKeystoneId>> = {
  'more-projectiles': 'splinter', // every primary hit bursts into radial sub-arrows (PRD #419 slice D)
  'greater-projectile': 'comet', // the fireball hurls the struck enemy backward (PRD #419 slice E)
  chain: 'fork',
  explosion: 'cataclysm',
  'flame-strike': 'meteor',
  nova: 'serpent-spiral', // the ring's rays become piercing fire serpents
  'scorched-earth': 'combustion', // burning enemies explode on death (PRD #419 slice C)
};

// --- Pierce-ray machinery (ex Fire Serpent). Retained in service of Serpent
// Spiral, the nova keystone, whose rays still pierce - the Fire Serpent
// keystone itself was retired for Comet (PRD #419 slice E). ------------------

/**
 * Perpendicular half-width of the serpent's ray, in tiles: an enemy whose tile
 * centre lies within this of the caster->target line (and within range, beyond
 * the caster) is run through. Half a tile = the ray covers its own tile row.
 */
export const ARENA_PIERCE_RAY_HALF_WIDTH = 0.5;

/**
 * Whether `candidate` is skewered by a serpent flying from `from` through
 * `through` with reach `range`: on the ray's strip (perpendicular distance <=
 * {@link ARENA_PIERCE_RAY_HALF_WIDTH}), strictly beyond the caster, and within
 * range. `through` itself trivially qualifies. Degenerate (same-tile) aim
 * pierces nothing extra.
 */
export function isOnPierceRay(
  from: { x: number; y: number },
  through: { x: number; y: number },
  candidate: { x: number; y: number },
  range: number,
): boolean {
  const dx = through.x - from.x;
  const dy = through.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return false;
  const cx = candidate.x - from.x;
  const cy = candidate.y - from.y;
  const along = (cx * dx + cy * dy) / len; // distance along the ray
  if (along <= 0 || along > range) return false;
  const across = Math.abs(cx * dy - cy * dx) / len; // perpendicular distance
  return across <= ARENA_PIERCE_RAY_HALF_WIDTH;
}

/** The tile the serpent's flight visually ends on: `range` tiles from `from`
 * along the aim line (rounded) - it flies THROUGH the horde, not to one enemy. */
export function pierceRayEnd(
  from: { x: number; y: number },
  through: { x: number; y: number },
  range: number,
): { x: number; y: number } {
  const dx = through.x - from.x;
  const dy = through.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return { x: through.x, y: through.y };
  return { x: Math.round(from.x + (dx / len) * range), y: Math.round(from.y + (dy / len) * range) };
}

// --- Fork (Chain keystone): every hop splits in two at extra decay. ---------

/** How many links each chain hop splits into with Fork owned (1 without). */
export const ARENA_FORK_BRANCHES = 1;

/**
 * Per-hop decay factor while Fork is owned - steeper than the plain chain's
 * {@link ARENA_CHAIN_DAMAGE_FACTOR} (0.6): twice the links at extra decay, so
 * a packed field cascades exponentially without doubling total damage per hop.
 */
export const ARENA_FORK_DAMAGE_FACTOR = 0.5;

/** Links each hop spawns per live link: {@link ARENA_FORK_BRANCHES} with Fork, else 1. */
export function chainBranches(keystones: ArenaKeystones): number {
  return keystones.has('chain') ? ARENA_FORK_BRANCHES : 1;
}

/** Damage of chain hop `hop` (1-based) from a primary hit of `primaryDamage`,
 * decayed by the plain or forked factor - always derived from the primary. */
export function chainHopDamage(
  primaryDamage: number,
  hop: number,
  keystones: ArenaKeystones,
): number {
  void keystones; // Fork deliberately leaves the dependable trunk unchanged.
  return Math.round(primaryDamage * ARENA_CHAIN_DAMAGE_FACTOR ** hop);
}

/** A Fork leaf is half of its corresponding normal trunk hop, and never zero. */
export function forkBranchDamage(trunkDamage: number): number {
  return Math.max(1, Math.round(trunkDamage * ARENA_FORK_DAMAGE_FACTOR));
}

// --- Cataclysm (Explosion keystone): bigger blast that leaves burning ground.

/** Flat blast-radius bonus (tiles, before the area stat) while Cataclysm is owned. */
export const ARENA_CATACLYSM_RADIUS_BONUS = 1;

/** Chebyshev radius of the burning ground a Cataclysm blast leaves behind. */
export const ARENA_CATACLYSM_BURN_RADIUS = 1;

/** Ticks the Cataclysm ground burns. */
export const ARENA_CATACLYSM_BURN_TICKS = 60;

/** Fraction of the blast's splash the ground deals per burn interval. */
export const ARENA_CATACLYSM_BURN_FRACTION = 0.25;

/** Per-burn-interval damage of the ground a splash of `splashDamage` leaves. */
export function cataclysmBurnDamage(splashDamage: number): number {
  return Math.max(1, Math.round(splashDamage * ARENA_CATACLYSM_BURN_FRACTION));
}

// --- Meteor (Flame Strike keystone): the proc becomes a telegraphed star. ---

/** Ticks between a meteor's telegraph (the proc) and its impact - 1.5 seconds
 * of sparkle warning, long enough to read, short enough to matter. */
export const ARENA_METEOR_DELAY_TICKS = 30;

/** Chebyshev radius of the meteor's impact. */
export const ARENA_METEOR_RADIUS = 2;

/**
 * Meteor damage as a multiple of the flame-strike column it replaces: rarer
 * (one star per proc, landing late) but a genuinely big, earned hit.
 */
export const ARENA_METEOR_DAMAGE_FACTOR = 2;

/** Cap on meteors in the air at once - a maxed cascade procs dozens; past the
 * cap a proc simply fizzles (deterministically - the roll is still drawn). */
export const ARENA_MAX_PENDING_METEORS = 8;

/** The impact damage of a meteor procced by a hit of `hitDamage`. */
export function meteorDamage(hitDamage: number): number {
  return Math.round(flameStrikeDamage(hitDamage) * ARENA_METEOR_DAMAGE_FACTOR);
}

// --- Combustion (Scorched Earth keystone, PRD #419 slice C): enemies that ---
// die while BURNING explode in a mushroom cloud, splashing a fraction of
// their OWN max HP to neighbors - corpse-HP-derived, so the detonation scales
// with the horde's geometric HP curve by construction at every tier (the
// skill-derived alternative would decay against it - the exact arithmetic-dead
// mistake the trail rework fixed). Cascades are gated on the pack actually
// being on fire: a splash-killed neighbor that was itself burning detonates
// too; an unburned corpse never chains.

/**
 * Ticks an enemy counts as **burning** after any zone burn (entry or interval,
 * from any burning ground - trail, Cataclysm ground, meteor pool: the burning
 * *state* is zone-generic even though the detonation is Scorched Earth's
 * keystone). One second - long enough that a projectile kill on a just-burned
 * enemy still detonates, short enough that "died burning" stays honest. Must
 * be >= {@link ARENA_ZONE_BURN_INTERVAL_TICKS} so a dweller never flickers
 * un-burning between interval ticks.
 */
export const ARENA_BURNING_WINDOW_TICKS = 20;

/** Fraction of the corpse's `health.max` (the table max - per-corpse and
 * idempotent, never a running value) the detonation splashes to each neighbor. */
export const ARENA_COMBUSTION_FRACTION = 0.2;

/** Chebyshev radius of the detonation before the area stat - tight by design:
 * the cascade is the payoff, one bomb is not. */
export const ARENA_COMBUSTION_RADIUS = 1;

/** Splash one detonating corpse of `maxHealth` deals to each live neighbor
 * (floored at 1 - a detonation always hurts). */
export function combustionDamage(maxHealth: number): number {
  return Math.max(1, Math.round(maxHealth * ARENA_COMBUSTION_FRACTION));
}

/** The detonation's reach in tiles, area-stat scaled like every other radius. */
export function combustionRadius(areaStatLevel: number): number {
  return ARENA_COMBUSTION_RADIUS * (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel);
}

// --- Splinter (More Projectiles keystone, PRD #419 slice D): on each PRIMARY
// projectile hit the shot bursts into 8 radial sub-arrows from the struck tile
// (the NOVA_RAY_DELTAS tile-walk machinery) - target-centered shrapnel, the
// complement of Nova's runner-centered ring. Gen-1 like pierced extras and
// nova rays: explosions/procs fire off a splinter hit, chains never re-root,
// and a splinter hit never splinters again.

/** The burst's reach in tiles at base area - deliberately shorter than
 * {@link ARENA_NOVA_RANGE}: shrapnel around the struck pack, not a second nova. */
export const ARENA_SPLINTER_RANGE = 4;

/**
 * Each sub-arrow's damage as a FLAT fraction of the primary hit - no per-ray
 * decay (all eight rays are peers), visibly weaker than the primary by design
 * (shrapnel, not free fireballs).
 */
export const ARENA_SPLINTER_DAMAGE_FACTOR = 0.4;

/** The burst's reach in tiles (0 while the keystone is unowned), area-stat
 * scaled like every other reach (the {@link novaRange} shape). */
export function splinterRange(keystones: ArenaKeystones, areaStatLevel: number): number {
  if (!keystones.has('more-projectiles')) return 0;
  return ARENA_SPLINTER_RANGE * (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel);
}

/** One sub-arrow's damage, derived flat from the primary projectile's hit. */
export function splinterDamage(projectileDamage: number): number {
  return Math.round(projectileDamage * ARENA_SPLINTER_DAMAGE_FACTOR);
}

// --- Comet (Greater Projectile keystone, PRD #419 slice E, replacing Fire ---
// Serpent): the fireball hits like a truck - each PRIMARY projectile hit
// hurls the struck enemy backward along the flight direction, walkability-
// clamped. The kit's missing crowd control: every cast shoves the front of
// the pack away, buying the stillness stop-to-cast needs. Target-only by
// design (the crowd shove stays Explosion's identity); deterministic, no rng.

/** Tiles a struck (surviving, non-boss) enemy is hurled along the flight line. */
export const ARENA_COMET_KNOCKBACK_TILES = 3;

/** The boss's knockback (0 = immune) - a tuning surface if immunity feels flat. */
export const ARENA_COMET_BOSS_KNOCKBACK_TILES = 0;

/** How many tiles a Comet hit shoves this enemy: 0 while the keystone is
 * unowned, the boss lever for the boss, the full knockback otherwise. */
export function cometKnockbackTiles(keystones: ArenaKeystones, boss: boolean): number {
  if (!keystones.has('greater-projectile')) return 0;
  return boss ? ARENA_COMET_BOSS_KNOCKBACK_TILES : ARENA_COMET_KNOCKBACK_TILES;
}

/**
 * The tile a Comet shove lands the struck enemy on: `tiles` unit steps out of
 * `strike` along the `from` -> `strike` flight direction (the float unit
 * vector of the pierce-ray math, each step's tile rounded - so a shallow
 * flight shoves along the shallow line, not a sign-snapped diagonal), clamped
 * by `isWalkable` - the walk stops on the LAST walkable tile before a block.
 * Consecutive rounds landing on the same tile collapse (a perfect diagonal
 * covers ~0.7 tiles per axis per step), so the path is king-move contiguous
 * and every intermediate tile is checked. Returns `null` when the shove moves
 * nothing: `tiles` <= 0, a degenerate same-tile aim, or the very first step
 * blocked.
 */
export function cometLanding(
  from: { x: number; y: number },
  strike: { x: number; y: number },
  tiles: number,
  isWalkable: (x: number, y: number) => boolean,
): { x: number; y: number } | null {
  if (tiles <= 0) return null;
  const dx = strike.x - from.x;
  const dy = strike.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  const ux = dx / len;
  const uy = dy / len;
  let landed: { x: number; y: number } | null = null;
  let lastX = strike.x;
  let lastY = strike.y;
  for (let i = 1; i <= tiles; i++) {
    const x = Math.round(strike.x + ux * i);
    const y = Math.round(strike.y + uy * i);
    if (x === lastX && y === lastY) continue; // a diagonal's duplicate round
    if (!isWalkable(x, y)) break; // clamp: stop on the last walkable tile
    landed = { x, y };
    lastX = x;
    lastY = y;
  }
  return landed;
}

// --- Meteor pools (PRD #419 slice B): the impact leaves molten burn ground. --

/** Chebyshev radius of the molten pool a meteor impact leaves - deliberately
 * tighter than {@link ARENA_METEOR_RADIUS}: area denial where the pack was
 * densest, not a second blast. */
export const ARENA_METEOR_POOL_RADIUS = 1;

/** Ticks the molten pool burns (~3s at the 50ms arena tick). */
export const ARENA_METEOR_POOL_TICKS = 60;

/** Fraction of the impact damage the pool deals per burn interval (the
 * {@link cataclysmBurnDamage} shape - derived, never an absolute constant). */
export const ARENA_METEOR_POOL_FRACTION = 0.25;

/** Per-burn-interval damage of the molten pool a meteor impact of
 * `impactDamage` leaves behind. */
export function meteorPoolDamage(impactDamage: number): number {
  return Math.max(1, Math.round(impactDamage * ARENA_METEOR_POOL_FRACTION));
}

// ---------------------------------------------------------------------------
// Ascensions (PRD #378): once modifiers, keystones, and stats are all owned,
// the pool never empties - an infinitely repeatable small pick. Deliberately
// plain; it keeps the ding beat alive to the horn.
// ---------------------------------------------------------------------------

/** The two repeatable ascensions: raw damage, or a faster volley. */
export type ArenaAscensionId = 'power' | 'alacrity';

/** Every ascension id, in the fixed order the pool offers them (determinism). */
export const ARENA_ASCENSION_IDS: readonly ArenaAscensionId[] = ['power', 'alacrity'];

/** +damage fraction per `power` ascension (multiplies every skill hit, from the base). */
export const ARENA_ASCENSION_DAMAGE_PER_PICK = 0.05;

/** Cooldown fraction cut per `alacrity` ascension (the volley floor still holds). */
export const ARENA_ASCENSION_COOLDOWN_CUT_PER_PICK = 0.03;

/** The build's ascension counts, keyed by id (absent = none taken). */
export type ArenaAscensions = Partial<Record<ArenaAscensionId, number>>;

/**
 * Hard cap on FX events one volley may cascade into - bounds both the tick's
 * work and the owner-only `ArenaFx` message size. Re-budgeted 128 -> 256 for
 * the keystones (PRD #378): a maxed UN-forked build (6 projectiles x 5 chain
 * hops, explosion + flame strike on every hit) tops out around ~108 events and
 * always renders whole; the Fork keystone's branching tree is exponential by
 * design (6 x (1 + 2+4+8+16+32) hits = 378, each hit up to two on-hit events
 * ~= 1100 worst-case), so the deepest forked melts trim - damage already
 * applied stays applied; the cap trims *events*, and the cascade stops
 * growing with them (the graceful-trim contract, unchanged).
 */
export const ARENA_MAX_CASCADE_EVENTS = 256;

// ---------------------------------------------------------------------------
// Passive stats (the draft pool's other half - unchanged from ADR-0175).
// ---------------------------------------------------------------------------

/** The seven draftable passive stats (PRD #334 user stories 22-23; `magnet`
 * grows the XP-orb pickup radius, PRD #431 Slice A - the seventh, the first stat
 * to touch the orb loop the runner interacts with every second). */
export type ArenaStatId =
  | 'damage'
  | 'cooldown'
  | 'area'
  | 'regen'
  | 'xp-gain'
  | 'mobility'
  | 'magnet';

/** Every stat id, in the fixed order the draft pool is built in (determinism). */
export const ARENA_STAT_IDS: readonly ArenaStatId[] = [
  'damage',
  'cooldown',
  'area',
  'regen',
  'xp-gain',
  'mobility',
  'magnet',
];

/** Highest level any passive stat can be drafted to. */
export const ARENA_STAT_MAX_LEVEL = 5;

/** +damage fraction per `damage` stat level (multiplies every skill hit). */
export const ARENA_DAMAGE_PER_STAT_LEVEL = 0.1;

/** Cooldown fraction cut per `cooldown` stat level (volleys fire sooner). */
export const ARENA_COOLDOWN_CUT_PER_STAT_LEVEL = 0.08;

/** +reach fraction per `area` stat level (skill range, explosion + flame-strike radii). */
export const ARENA_AREA_PER_STAT_LEVEL = 0.1;

/**
 * HP restored per `regen` stat level, each {@link ARENA_REGEN_INTERVAL_TICKS}
 * (i.e. per 2s) - so the effective sustain is **0.5 HP/level/second**. Dialled
 * down hard from the original 2 HP/level/second (a quarter of the old rate): at
 * that strength a couple of `regen` picks out-healed the horde and made runs a
 * war of attrition the runner always won. Regen is now a top-up between hits,
 * not a wall (endless-arena retune).
 */
export const ARENA_REGEN_HP_PER_STAT_LEVEL = 1;

/** Ticks between passive-regen heals (40 ticks = 2s at STEP_MS 50). */
export const ARENA_REGEN_INTERVAL_TICKS = 40;

/** +XP fraction per `xp-gain` stat level (multiplies every kill's XP). */
export const ARENA_XP_GAIN_PER_STAT_LEVEL = 0.15;

/** Gait (ms-per-tile) fraction cut per `mobility` stat level (kite harder). */
export const ARENA_MOVE_SPEED_CUT_PER_STAT_LEVEL = 0.08;

/**
 * Tiles added to the XP-orb pickup radius per `magnet` stat level (PRD #431
 * Slice A). The effective radius is the base
 * {@link ./xpGem.XP_GEM_MAGNET_RADIUS_TILES} (2.5) plus this per level, so a
 * maxed magnet reaches 2.5 + 0.75×5 = 6.25 tiles - a meaningful pull for the
 * parked-caster playstyle (which starves at 2.5 by design, ADR-0215), still
 * well inside the skill's range 10 and never screen-sized. This is the
 * *sanctioned* way to grow the magnet: ADR-0215 explicitly warns against
 * inflating the base constant, so the growth is a draft choice, not a default.
 * The effective-radius math is {@link ./xpGem.arenaMagnetRadius}.
 */
export const ARENA_MAGNET_TILES_PER_LEVEL = 0.75;

// ---------------------------------------------------------------------------
// Effective-number math (modifiers x passive stats). All pure; all derive from
// the table base so repeated application never compounds.
// ---------------------------------------------------------------------------

/** Projectiles per volley for the drafted modifier levels. */
export function skillProjectileCount(mods: ArenaModifierLevels): number {
  return 1 + ARENA_PROJECTILES_PER_LEVEL * (mods['more-projectiles'] ?? 0);
}

/** The empty keystone set, for callers that predate (or don't care about) keystones. */
const NO_KEYSTONES: ArenaKeystones = new Set();

/** The projectile's look: an arrow until Greater Projectile transforms it into
 * a fireball - and a fireball it stays at keystone max: Comet (PRD #419 slice
 * E) shoves rather than reskinning, so the old Fire Serpent `fire-snake`
 * return is gone (the serpent art still flies on Serpent Spiral's nova rays). */
export function skillProjectileKind(mods: ArenaModifierLevels): ArenaProjectileKind {
  return (mods['greater-projectile'] ?? 0) >= 1 ? 'fireball' : 'arrow';
}

/** Art size tier for the client (0 = arrow; 1..max = fireball, growing). */
export function skillProjectileSizeTier(mods: ArenaModifierLevels): number {
  return mods['greater-projectile'] ?? 0;
}

/**
 * Damage one primary projectile deals: base, grown by Greater Projectile,
 * trimmed by the multi-projectile "less" factor while breadth is owned, scaled
 * by the `damage` passive.
 */
export function skillProjectileDamage(
  mods: ArenaModifierLevels,
  damageStatLevel: number,
  powerAscensions = 0,
): number {
  const greater = 1 + ARENA_GREATER_DAMAGE_PER_LEVEL * (mods['greater-projectile'] ?? 0);
  const less = (mods['more-projectiles'] ?? 0) > 0 ? ARENA_MULTI_PROJECTILE_LESS_FACTOR : 1;
  const ascended = 1 + ARENA_ASCENSION_DAMAGE_PER_PICK * powerAscensions;
  return Math.round(
    ARENA_SKILL_DAMAGE *
      greater *
      less *
      (1 + ARENA_DAMAGE_PER_STAT_LEVEL * damageStatLevel) *
      ascended,
  );
}

/** Ticks between volleys with `cooldownStatLevel` drafted (floored); `alacrity`
 * ascensions keep shaving it toward the same floor (derive-from-base as ever).
 * `baseTicks` is the class's base cadence (PRD #446): defaults to the fire
 * mage's constant so the mage is byte-identical; the archer passes its own
 * (faster) base, and every stat/ascension scale applies to it the same way. */
export function skillCooldownTicks(
  cooldownStatLevel: number,
  alacrityAscensions = 0,
  baseTicks = ARENA_SKILL_COOLDOWN_TICKS,
): number {
  const ascended = Math.max(0, 1 - ARENA_ASCENSION_COOLDOWN_CUT_PER_PICK * alacrityAscensions);
  return Math.max(
    ARENA_MIN_SKILL_COOLDOWN_TICKS,
    Math.round(
      baseTicks * (1 - ARENA_COOLDOWN_CUT_PER_STAT_LEVEL * cooldownStatLevel) * ascended,
    ),
  );
}

/** Targeting reach in tiles (compared against Chebyshev distance). `baseRange`
 * is the class's base reach (PRD #446): defaults to the fire mage's constant
 * (byte-identical); the archer passes its own longer range. */
export function skillRange(areaStatLevel: number, baseRange = ARENA_SKILL_RANGE): number {
  return baseRange * (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel);
}

/** Explosion radius in tiles for the drafted levels (0 while the modifier is
 * unowned); the Cataclysm keystone adds its flat bonus before the area scale. */
export function explosionRadius(
  mods: ArenaModifierLevels,
  areaStatLevel: number,
  keystones: ArenaKeystones = NO_KEYSTONES,
): number {
  const level = mods.explosion ?? 0;
  if (level <= 0) return 0;
  const cataclysm = keystones.has('explosion') ? ARENA_CATACLYSM_RADIUS_BONUS : 0;
  const base =
    ARENA_EXPLOSION_RADIUS_BASE + ARENA_EXPLOSION_RADIUS_PER_LEVEL * (level - 1) + cataclysm;
  return base * (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel);
}

/** Splash damage of an explosion triggered by a hit of `hitDamage`. */
export function explosionDamage(hitDamage: number, mods: ArenaModifierLevels): number {
  const level = mods.explosion ?? 0;
  if (level <= 0) return 0;
  const fraction = Math.min(
    ARENA_EXPLOSION_FRACTION_CAP,
    ARENA_EXPLOSION_FRACTION_BASE + ARENA_EXPLOSION_FRACTION_PER_LEVEL * (level - 1),
  );
  return Math.round(hitDamage * fraction);
}

/** Chain hops each primary hit cascades into (0 while unowned). */
export function chainHops(mods: ArenaModifierLevels): number {
  return ARENA_CHAIN_HOPS_PER_LEVEL * (mods.chain ?? 0);
}

/** Damage of chain hop `hop` (1-based) spawned by a primary hit of `primaryDamage`. */
export function chainDamage(primaryDamage: number, hop: number): number {
  return Math.round(primaryDamage * ARENA_CHAIN_DAMAGE_FACTOR ** hop);
}

/** Flame-strike proc chance per secondary hit (0 while unowned, capped at 1). */
export function flameStrikeChance(mods: ArenaModifierLevels): number {
  return Math.min(1, ARENA_FLAME_STRIKE_CHANCE_PER_LEVEL * (mods['flame-strike'] ?? 0));
}

/** Flame-strike column radius in tiles (area-scaled like the explosion). */
export function flameStrikeRadius(mods: ArenaModifierLevels, areaStatLevel: number): number {
  if ((mods['flame-strike'] ?? 0) <= 0) return 0;
  return ARENA_FLAME_STRIKE_RADIUS * (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel);
}

/** Damage the flame-strike column deals, from the hit of `hitDamage` that procced it. */
export function flameStrikeDamage(hitDamage: number): number {
  return Math.round(hitDamage * ARENA_FLAME_STRIKE_DAMAGE_FRACTION);
}

/** The runner's effective gait for a `mobility` stat level, derived from the base gait. */
export function arenaMoveMsPerTile(baseMsPerTile: number, moveSpeedStatLevel: number): number {
  return Math.round(baseMsPerTile * (1 - ARENA_MOVE_SPEED_CUT_PER_STAT_LEVEL * moveSpeedStatLevel));
}

/** Effective post-movement cast gate. Moving-fire classes have no settle gate. */
export function arenaCastSettleTicks(
  baseTicks: number,
  mobilityLevel: number,
  firesWhileMoving: boolean,
): number {
  return firesWhileMoving ? 0 : Math.max(1, baseTicks - mobilityLevel);
}

// ---------------------------------------------------------------------------
// The XP level curve (cadence retune, PRD #378: mildly geometric, replacing
// ADR-0175's linear ramp). The felt problem was structural: flat per-kill XP
// within a roster tier against linearly-rising costs stretched each ding
// longer than the last (the drought), then the tier boundary's income jump
// banked several at once (the boom). A geometric cost curve tracks the
// smoothed income ramp (see ARENA_STAGES' per-tier `xp`, tuned inversely to
// `waveSize`), so dings land on a steady beat all run - the cadence-guard
// test in arena.test.ts pins the band.
// ---------------------------------------------------------------------------

/** XP the first level-up (level 1 -> 2) costs - the first-draft pin (unchanged). */
export const ARENA_XP_BASE = 30;

/** Per-level cost growth factor (~+15%/level - the mildly geometric ramp). */
export const ARENA_XP_GROWTH = 1.15;

/** XP needed to level up *from* `level` (a mildly geometric ramp - the tuning surface). */
export function arenaXpToNext(level: number): number {
  return Math.round(ARENA_XP_BASE * ARENA_XP_GROWTH ** (level - 1));
}

/** XP a kill worth `baseXp` grants with `xpGainStatLevel` drafted. */
export function arenaXpGain(baseXp: number, xpGainStatLevel: number): number {
  return Math.round(baseXp * (1 + ARENA_XP_GAIN_PER_STAT_LEVEL * xpGainStatLevel));
}
